// MUST be first - capture console errors before any other code runs
import { initConsoleLogCapture } from '../../utils/consoleLogBuffer';
initConsoleLogCapture();

// Auto error reporter — wraps console.error/window.error/unhandledrejection
// and POSTs deduplicated reports. Order matters: this runs AFTER the log
// buffer wrap so the buffer still captures all entries (the reporter calls
// the buffer's wrap as its passthrough).
import { initErrorReporter } from '../../utils/errorReporter';
initErrorReporter();

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import App from './App.tsx';
import '../../index.css';
import { isSupabaseConfigured } from '../../utils/supabase';
import { StartupOrchestrator } from '../../utils/startupOrchestrator';
import { testAuthentication } from '../../utils/testAuth';
import { createComponentLogger } from '../../utils/logger';
import { performanceTracker } from '../../utils/performanceTracker';

// Initialize component logger
const log = createComponentLogger('Main', 'initialization');

// Mark app mount time
performanceTracker.mark('app_mount');

// Log deployment info (development only)
if (import.meta.env.DEV) {
  log.info('Application starting', {
    deployTimestamp: new Date().toISOString(),
    supabaseConfigured: isSupabaseConfigured(),
    environment: import.meta.env.MODE
  });
}

// Initialize consolidated startup cleanup
StartupOrchestrator.initialize();

// Development utilities - only expose in development environment
if (import.meta.env.DEV && typeof window !== 'undefined') {
  interface TestWindow extends Window {
    testAuth: typeof testAuthentication;
    testGmailTokenStorage?: typeof import('../../utils/testGmailTokens').testGmailTokenStorage;
    GmailService?: typeof import('../../services/gmailService').GmailService;
  }
  
  const testWindow = window as unknown as TestWindow;
  testWindow.testAuth = testAuthentication;
  
  log.debug('Development utilities exposed to window object', {
    utilities: ['testAuth']
  });

  // Expose Gmail utilities
  Promise.all([
    import('../../utils/testGmailTokens').then(({ testGmailTokenStorage }) => {
      testWindow.testGmailTokenStorage = testGmailTokenStorage;
    }),
    import('../../services/gmailService').then(({ GmailService }) => {
      testWindow.GmailService = GmailService;
    })
  ]).then(() => {
    log.debug('Gmail development utilities exposed');
  }).catch(() => {
    // Silently handle failure - not critical
  });

  // Services exposed to window for development debugging (silent)
}

// React StrictMode configuration
const isDevelopment = import.meta.env.DEV;

createRoot(document.getElementById('root')!).render(
  isDevelopment ? (
    <StrictMode>
      <App />
    </StrictMode>
  ) : (
    <App />
  )
);
